import { forwardRef } from "react";

import { cx, styled } from "simplydo/react/styled";
import { mobileOnly } from "simplydo/react/theme";

import { alignItems, gapCss, gridTemplate, textAlignCss } from "./Grid.styles";
import type { GridProps } from "./Grid.types";
import { GridColumn } from "./GridColumn";
import { GridRow } from "./GridRow";

type GridStyle = {
  $columns: number;
  $centered?: boolean;
  $verticalAlign?: string;
  $textAlign?: string;
  $gap?: string | number;
};

const GridRoot = styled.div<GridStyle>`
  flex: 1;
  display: grid;
  grid-template-columns: ${({ $columns }) => gridTemplate($columns)};
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  gap: ${({ $gap }) => gapCss($gap)};
  justify-items: ${({ $centered }) => ($centered ? "center" : "stretch")};
  align-items: ${({ $verticalAlign }) => alignItems($verticalAlign)};
  text-align: ${({ $textAlign }) => textAlignCss($textAlign)};

  &.is-centered > ${GridColumn} {
    align-self: center !important;
  }

  &.is-stackable {
    ${mobileOnly} {
      grid-template-columns: 1fr;
      > ${GridColumn},
      > ${GridRow} {
        grid-column: 1 / -1;
        width: 100%;
        max-width: 100%;
      }
      > ${GridRow} {
        grid-template-columns: 1fr;
      }
      > ${GridRow} > ${GridColumn} {
        grid-column: 1 / -1;
        width: 100%;
        max-width: 100%;
      }
    }
  }
`;

const GridComponent = forwardRef<HTMLDivElement, GridProps>((props, ref) => {
  const {
    columns = 1,
    stackable,
    centered,
    verticalAlign,
    textAlign,
    gap,
    children,
    className,
    style,
    ...rest
  } = props;
  return (
    <GridRoot
      ref={ref}
      className={cx(
        stackable && "is-stackable",
        centered && "is-centered",
        className,
      )}
      style={style}
      $columns={columns}
      $centered={centered}
      $verticalAlign={verticalAlign}
      $textAlign={textAlign}
      $gap={gap}
      {...rest}
    >
      {children}
    </GridRoot>
  );
});

GridComponent.displayName = "Grid";

export const Grid = styled(GridComponent)``;
